/** Run-agent button: triggers /api/agents/run for the agent that owns this stage.
 * Shows real-time status, surfaces structured backend errors as friendly toasts,
 * and keeps the button disabled while a request is in-flight.
 *
 * iter70.1 — When the backend refuses a storyboard run with code
 * `detailed_production_script_required` (no approved DPS), we surface an
 * explicit confirm modal. If the operator confirms, we re-send with
 * `confirm_dps_override: true`. AUTO pipelines NEVER set this flag.
 */
import { useEffect, useRef, useState } from "react";
import { Sparkles, RefreshCcw, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const SLOW_HINT_AFTER_MS = 30_000;
const HARD_CAP_MS = 90_000;
const MAX_AUTO_RETRIES = 2;

export default function RunAgentButton({ project, agent, label, extra, onDone, disabled = false }) {
    const [busy, setBusy] = useState(false);
    const [slow, setSlow] = useState(false);
    const [dpsOverridePrompt, setDpsOverridePrompt] = useState(null);  // {checkpoint, message}
    const slowTimerRef = useRef(null);
    const lastFailedRef = useRef(false);

    useEffect(() => () => {
        if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    }, []);

    const doRun = async ({ confirmDpsOverride = false } = {}) => {
        setBusy(true);
        setSlow(false);
        slowTimerRef.current = setTimeout(() => setSlow(true), SLOW_HINT_AFTER_MS);

        // Hard cap on UI side — if the request hasn't returned in 90s we give up
        // (backend's own ceiling is 75s, so this is just a safety net).
        const controller = new AbortController();
        const hardCap = setTimeout(() => controller.abort(), HARD_CAP_MS);

        let lastErr = null;
        for (let attempt = 1; attempt <= MAX_AUTO_RETRIES; attempt++) {
            try {
                const body = {
                    project_id: project.id,
                    agent,
                    extra_input: extra || null,
                };
                if (agent === "storyboard" && confirmDpsOverride) {
                    body.confirm_dps_override = true;
                }
                const { data } = await api.post(
                    "/agents/run",
                    body,
                    { signal: controller.signal, timeout: HARD_CAP_MS },
                );
                lastFailedRef.current = false;
                toast.success(`${label || agent} done.`);
                onDone && onDone(data);
                lastErr = null;
                break;
            } catch (e) {
                lastErr = e;
                const status = e?.response?.status;
                const code = e?.response?.data?.detail?.code;
                // iter70.1 — storyboard-specific DPS override flow.
                if (
                    agent === "storyboard"
                    && status === 409
                    && code === "detailed_production_script_required"
                    && !confirmDpsOverride
                ) {
                    clearTimeout(hardCap);
                    if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
                    setBusy(false);
                    setSlow(false);
                    setDpsOverridePrompt({
                        message: e?.response?.data?.detail?.message
                            || "Storyboard requires an approved Detailed Production Script.",
                        checkpoint: e?.response?.data?.detail?.checkpoint || {},
                    });
                    return;
                }
                // Don't auto-retry: budget exhausted, auth, bad input
                if ([400, 401, 402, 403, 404, 409].includes(status)) break;
                // Don't auto-retry on client-side abort
                if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") break;
                if (attempt < MAX_AUTO_RETRIES) {
                    toast.message(`${label || agent} retrying…`, { duration: 1500 });
                    await new Promise((r) => setTimeout(r, 1500));
                }
            }
        }
        clearTimeout(hardCap);
        if (slowTimerRef.current) clearTimeout(slowTimerRef.current);

        if (lastErr) {
            lastFailedRef.current = true;
            const detail = lastErr?.response?.data?.detail;
            const msg =
                lastErr?.userMessage
                || (typeof detail === "string" ? detail : detail?.message)
                || (lastErr?.code === "ERR_CANCELED" ? "Took longer than 90 seconds. Please retry." : null)
                || "Agent failed. Please retry.";
            toast.error(msg);
        }
        setBusy(false);
        setSlow(false);
    };

    const run = () => doRun({ confirmDpsOverride: false });

    return (
        <div className="flex flex-col items-start gap-1">
            <button
                data-testid={`run-${agent}`}
                onClick={run}
                disabled={busy || disabled}
                className="btn-accent text-xs px-3 py-1.5 disabled:opacity-60 disabled:cursor-wait"
            >
                {lastFailedRef.current ? (
                    <RefreshCcw className="w-3.5 h-3.5" />
                ) : (
                    <Sparkles className="w-3.5 h-3.5" />
                )}
                {busy
                    ? slow
                        ? "Still working… (up to 75s)"
                        : "Processing…"
                    : lastFailedRef.current
                        ? `Retry ${label || agent}`
                        : (label || `Run ${agent}`)}
            </button>
            {slow && (
                <span className="text-[10px] font-mono text-amber-400 uppercase tracking-wider">
                    Taking longer than expected — hold tight
                </span>
            )}

            {/* iter70.1 — Storyboard manual override confirm modal */}
            {dpsOverridePrompt && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
                    data-testid="dps-override-modal"
                    onClick={() => setDpsOverridePrompt(null)}
                >
                    <div
                        className="bg-zinc-950 border border-amber-500/40 rounded-sm p-5 max-w-md w-full mx-4 space-y-3"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-400" />
                            <h4 className="font-display text-base text-zinc-100">
                                No approved Detailed Production Script
                            </h4>
                        </div>
                        <p className="text-[12px] text-zinc-300">
                            {dpsOverridePrompt.message}
                        </p>
                        <p className="text-[11px] text-amber-300/90 italic">
                            Storyboard will be generated from the compact short-script
                            only. Character dialogue mapping, environment mapping, and
                            reference-requirement tracking will <strong>not</strong> be
                            available for this run.
                        </p>
                        <p className="text-[11px] text-zinc-500 italic">
                            AUTO pipelines never use this fallback — this override
                            applies only to the current manual run.
                        </p>
                        <div className="flex items-center justify-end gap-2 pt-1">
                            <button
                                type="button"
                                className="btn-secondary text-xs"
                                onClick={() => setDpsOverridePrompt(null)}
                                data-testid="dps-override-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn-primary text-xs"
                                onClick={() => {
                                    setDpsOverridePrompt(null);
                                    doRun({ confirmDpsOverride: true });
                                }}
                                data-testid="dps-override-confirm"
                            >
                                Override and run Storyboard
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
